import { motion } from "framer-motion";
import { ScrollText } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { EULA_SECTIONS, NDA_SECTIONS, PRIVACY_SECTIONS, REFUNDS_SECTIONS, MARKETPLACE_TERMS_SECTIONS, CREATOR_POLICY_SECTIONS } from "@/content/legal";

const VARIANTS = {
  eula: {
    overline: "Specpixel Entertainment PTY LTD",
    title: "End User License Agreement",
    intro: "Terms and conditions of service. Use of any Specpixel Entertainment PTY LTD service commits you under this policy.",
    sections: EULA_SECTIONS,
  },
  marketplace: {
    overline: "Specpixel Entertainment PTY LTD",
    title: "Marketplace & NDIS Compliance Terms",
    intro: "Marketplace sale terms, Centrelink DSP income-protection mechanics, NDIS program tracks and line items, and our universal accessibility statement.",
    sections: MARKETPLACE_TERMS_SECTIONS,
  },
  nda: {
    overline: "Specpixel Entertainment PTY LTD",
    title: "Confidentiality Agreement",
    intro: "Non-disclosure agreement between Specpixel and its clients. By working with Specpixel, you hereby agree to this NDA.",
    sections: NDA_SECTIONS,
  },
  creator: {
    overline: "New Citizen · Specpixel Entertainment PTY LTD",
    title: "Creator & Monetization Policy",
    intro: "The open-market rules for New Citizen server owners and modders: you own your work, you keep 100% of what you earn, and prohibited content is always removed — no exceptions, no matter how much it makes.",
    sections: CREATOR_POLICY_SECTIONS,
  },
  privacy: {
    overline: "Specpixel Entertainment PTY LTD",
    title: "Privacy Policy",
    intro: "How we collect, use, store and disclose personal information under the Privacy Act 1988 (Cth) and the Australian Privacy Principles.",
    sections: PRIVACY_SECTIONS,
  },
  refunds: {
    overline: "Specpixel Entertainment PTY LTD",
    title: "Refund Policy",
    intro: "Your rights under the Australian Consumer Law and how refunds work across every Specpixel program.",
    sections: REFUNDS_SECTIONS,
  },
};

export default function LegalPage({ variant }) {
  const doc = VARIANTS[variant] || VARIANTS.eula;

  return (
    <div data-testid={`legal-${variant}-page`} className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase mb-4">{doc.overline}</p>
          <h1 data-testid="legal-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50 flex items-center gap-4">
            <ScrollText className="w-8 h-8 text-nc-blue shrink-0" /> {doc.title}
          </h1>
          <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">{doc.intro}</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 w-full flex flex-col gap-6">
        {doc.sections.map((section, i) => (
          <motion.section
            key={section.heading}
            data-testid={`legal-section-${i + 1}`}
            className="border border-white/10 bg-black/40 p-6"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.3) }}
          >
            <h2 className="font-sans font-bold uppercase text-lg text-nc-blue mb-4">{section.heading}</h2>
            <div className="flex flex-col gap-3">
              {section.paragraphs.map((p, j) => (
                <p key={j} className="font-mono text-xs text-zinc-400 leading-relaxed">{p}</p>
              ))}
            </div>
          </motion.section>
        ))}
      </main>

      <SiteFooter />
    </div>
  );
}
